import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/apiClient';
import { useAuth } from '@/context/AuthContext';
import type { GroupMember } from '@flock-types/chat.types';

interface MembersResponse {
  success: boolean;
  data: Array<{
    id: string;
    name: string;
    profile_image_url?: string;
    role: 'leader' | 'member';
  }>;
}

/** Community-group members — used for @mention autocomplete and DM targets. */
export function useGroupMembers(groupId: string | null | undefined) {
  const { currentOrganization, isSwitchingOrg } = useAuth();
  const orgId = currentOrganization?.id;

  const query = useQuery({
    queryKey: [orgId, groupId, 'group-members'],
    enabled: Boolean(groupId && orgId && !isSwitchingOrg),
    queryFn: async (): Promise<GroupMember[]> => {
      const response = await api.get<MembersResponse>(`/groups/${groupId}/members`);
      return (response.data || []).map((m) => ({
        userId: m.id,
        userName: m.name,
        profileImageUrl: m.profile_image_url,
        role: m.role,
      }));
    },
    staleTime: 5 * 60_000,
  });

  return { members: query.data ?? [], loading: query.isLoading };
}

export default useGroupMembers;
